import { matchPath, useLocation, useNavigate } from 'react-router';
import { useLocalStorageState, useUpdateEffect } from 'ahooks';
import {
  AUTHORIZATION_FLOW,
  Flow,
  LOGIN_FLOW,
  OPENAPP_FLOW,
  RECOVERY_FLOW,
  REGISTER_FLOW,
  RESET_FLOW,
  THIRD_FLOW,
} from '@/constants/flow';
import { type BizState, useBizStore } from '@/store/use-biz-store';
import { useFlowStore } from '@/store/use-flow-store';
import { validateUrl } from '@/utils/string';

const list = [
  { pathname: '/preLogin', flows: LOGIN_FLOW, defaultFlow: Flow.LOGIN_STEP_INPUT_USERNAME },
  { pathname: '/register', flows: REGISTER_FLOW, defaultFlow: Flow.REGISTER_STEP_INPUT_USERINFO },
  { pathname: '/resetPassword', flows: RESET_FLOW, defaultFlow: Flow.RESET_PASSWORD_STEP_INPUT_USERNAME },
  { pathname: '/recovery', flows: RECOVERY_FLOW, defaultFlow: Flow.RECOVERY_STEP_INPUT_USERNAME },
  { pathname: '/thirdname/:thirdname', flows: THIRD_FLOW, defaultFlow: Flow.THIRD_STEP_SOCIAL_LOGIN },
  { pathname: '/openapp', flows: OPENAPP_FLOW, defaultFlow: Flow.LOGIN_STEP_OPEN_APP },
  { pathname: '/oauthgrant', flows: AUTHORIZATION_FLOW, defaultFlow: Flow.LOGIN_STEP_AUTHORIZATION },
];

export function useFlow() {
  const { flow, prevFlow, setFlow: set } = useFlowStore();
  const setBizState = useBizStore((b) => b.setBizState);
  const [originURI] = useLocalStorageState('originURI');

  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const setFlow = (
    flow: Flow,
    params?: {
      resetBiz?: boolean;
      state?: Partial<BizState>;
    },
  ) => {
    if (params?.resetBiz) setBizState({ email: '', phone: '', userName: '' });
    else if (params?.state) setBizState(params.state);

    set(flow);

    const item = list.find((item) => item.flows.includes(flow));

    // Navigate when the target flow belongs to a different route.
    if (item && !matchPath(item.pathname, pathname)) {
      const matchThirdRoute = matchPath('/thirdname/:thirdname', item.pathname);
      if (matchThirdRoute) {
        const thirdname = matchThirdRoute.params.thirdname || 'msn';
        navigate({ pathname: `/thirdname/${thirdname}`, search: location.search });
      } else if (['/openapp', '/oauthgrant'].includes(item.pathname)) {
        navigate({ pathname: item.pathname, search: location.search });
      } else {
        // if current is third flow and has originURI, then navigate to originURI
        const { isValid } = validateUrl(window.location.href, ['ctx']);
        const uri = !isValid ? originURI || window.location.href : window.location.href;
        const { search } = new URL(uri as string);
        navigate({ pathname: item.pathname, search });
      }
    }
  };

  return { flow, prevFlow, setFlow };
}

export function useCurrentFlow() {
  const { pathname } = useLocation();
  const { flow, setFlow } = useFlow();

  // listen the Browser forward and backward navigation, correct flow based on pathname
  useUpdateEffect(() => {
    const item = list.find((item) => matchPath(item.pathname, pathname) && !item.flows.includes(flow));
    if (item) setFlow(item.defaultFlow, { resetBiz: true });
  }, [pathname]);

  return { flow };
}
